import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import Modal from "@/features/shared/components/Modal/Modal";
import getUserDisplayName from "@/features/timeOff/utils/getUserDisplayName";
const formatDateTime = (date) => {
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(date));
    }
    catch (error) {
        return date;
    }
};
// Champs à afficher en français
const translateField = (field) => {
    const map = {
        type: "Type",
        reason: "Raison",
        status: "Statut",
        manager: "Manager",
        decided_at: "Décidé le",
        comment: "Commentaire",
        start_at: "Début",
        end_at: "Fin",
        start_date: "Date de début",
        end_date: "Date de fin",
    };
    return map[field] ?? field.replace(/_/g, " ");
};
const formatStatus = (status) => {
    if (!status)
        return "-";
    const map = {
        pending: "en attente",
        approved: "approuvée",
        rejected: "refusée",
    };
    return map[status] ?? status;
};
const isDateLikeField = (field) => /(_at$|date)/.test(field);
const tryFormatDate = (value, withTime) => {
    if (typeof value !== "string")
        return String(value);
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime()))
            return value;
        if (withTime)
            return formatDateTime(value);
        return new Intl.DateTimeFormat("fr-FR", {
            year: "numeric",
            month: "short",
            day: "2-digit",
        }).format(d);
    }
    catch {
        return value;
    }
};
const formatValue = (field, value) => {
    if (value === null || value === undefined)
        return "-";
    if (field === "status")
        return formatStatus(String(value));
    if (isDateLikeField(field)) {
        const withTime = /_at$/.test(field);
        return String(tryFormatDate(value, withTime));
    }
    return String(value);
};
const translateAction = (action) => {
    const map = {
        created: "Créée",
        updated: "Mise à jour",
        deleted: "Supprimée",
        status_changed: "Statut modifié",
        comment_added: "Commentaire ajouté",
    };
    return map[action] ?? action.replace(/_/g, " ");
};
const formatActor = (event) => {
    if (!event.actor)
        return "Utilisateur supprimé";
    const { first_name, last_name, email } = event.actor;
    const fullName = [first_name, last_name].filter(Boolean).join(" ");
    return fullName || email || "Utilisateur";
};
const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
        return null;
    }
    return (_jsx("div", { className: "mt-2 space-y-1 text-xs text-gray-600", children: Object.entries(changes).map(([field, value]) => {
            const isDetailedChange = typeof value === "object" && value !== null && "from" in value && "to" in value;
            if (isDetailedChange) {
                const detailedChange = value;
                return (_jsxs("div", { children: [_jsxs("span", { className: "font-semibold text-gray-700", children: [translateField(field), " :"] }), " ", formatValue(field, detailedChange.from), " \u2192 ", " ", formatValue(field, detailedChange.to)] }, field));
            }
            return (_jsxs("div", { children: [_jsxs("span", { className: "font-semibold text-gray-700", children: [translateField(field), " :"] }), " ", formatValue(field, value)] }, field));
        }) }));
};
const TimeOffHistoryModal = ({ request, history, isLoading, error, onClose, onRetry }) => (_jsx(Modal, { onClose: onClose, children: _jsxs("div", { className: "w-full max-w-2xl rounded-lg bg-white shadow-xl max-h-screen overflow-y-auto", children: [_jsxs("div", { className: "flex flex-col md:flex-row justify-between items-center border-b p-4 bg-gray-100", children: [_jsx("h2", { className: "text-lg font-semibold", children: "Historique de la demande" }), _jsx("button", { onClick: onClose, className: "text-gray-500 hover:text-gray-700", "aria-label": "Fermer", children: "\u2715" })] }), _jsx("div", { className: "mb-4 flex items-start justify-between gap-4 p-4", children: _jsx("div", { children: _jsx("p", { className: "text-sm text-gray-600", children: request ? (_jsxs(_Fragment, { children: [request.type, " \u2014 ", getUserDisplayName(request.user), " du", " ", formatDateTime(request.start_at), " au ", formatDateTime(request.end_at)] })) : ("Sélectionnez une demande pour consulter son historique.") }) }) }), isLoading && _jsx("p", { className: "text-sm text-gray-500", children: "Chargement de l'historique..." }), error && !isLoading && (_jsxs("div", { className: "rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700", children: [_jsx("p", { className: "font-semibold", children: "Impossible de r\u00E9cup\u00E9rer l'historique." }), _jsx("p", { className: "mt-1", children: error }), _jsx("button", { onClick: onRetry, className: "mt-2 inline-flex items-center rounded border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100", children: "R\u00E9essayer" })] })), !isLoading && !error && (_jsxs("div", { className: "space-y-3", children: [history.length === 0 && (_jsx("p", { className: "text-sm text-gray-500", children: "Aucun \u00E9v\u00E9nement enregistr\u00E9 pour cette demande." })), history.map((event) => (_jsx("div", { className: "rounded-md border border-gray-200 p-3", children: _jsxs("div", { className: "flex items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("div", { className: "text-sm font-semibold text-gray-900", children: translateAction(event.action) }), _jsxs("div", { className: "text-xs text-gray-600", children: ["Par ", formatActor(event)] }), (event.from_status || event.to_status) && (_jsxs("div", { className: "text-xs text-gray-700", children: ["Statut : ", formatStatus(event.from_status), " \u2192 ", formatStatus(event.to_status)] })), renderChanges(event.changes)] }), _jsx("div", { className: "text-xs text-gray-500", children: formatDateTime(event.created_at) })] }) }, event.id)))] }))] }) }));
export default TimeOffHistoryModal;
